import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import BrandLogo from './BrandLogo';

const DISMISS_KEY = 'eliteedge_install_prompt_dismissed_v1';
const PARENT_INSTALLED_KEY = 'eliteedge_parent_portal_installed_v1';
const COACH_INSTALL_BOTTOM = 'calc(184px + env(safe-area-inset-bottom, 0px))';
let capturedInstallPrompt = null;
let capturedAppInstalled = false;
const installPromptSubscribers = new Set();

function notifyInstallPromptSubscribers() {
  installPromptSubscribers.forEach((subscriber) => subscriber({
    deferredPrompt: capturedInstallPrompt,
    installed: capturedAppInstalled,
  }));
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    capturedInstallPrompt = event;
    notifyInstallPromptSubscribers();
  });

  window.addEventListener('appinstalled', () => {
    capturedInstallPrompt = null;
    capturedAppInstalled = true;
    notifyInstallPromptSubscribers();
  });
}

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua);
}

function isStandaloneMode() {
  if (typeof window === 'undefined') return false;
  const mediaStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = typeof navigator !== 'undefined' && navigator.standalone === true;
  return Boolean(mediaStandalone || iosStandalone);
}

function isParentInstallConfirmed() {
  try {
    return localStorage.getItem(PARENT_INSTALLED_KEY) === '1';
  } catch {
    return false;
  }
}

function confirmParentInstall() {
  try {
    localStorage.setItem(PARENT_INSTALLED_KEY, '1');
  } catch {
    // The current session can still hide the prompt when storage is unavailable.
  }
}

function isIosInAppBrowser() {
  if (!isIosDevice()) return false;
  const ua = navigator.userAgent || '';
  return /FBAV|FBAN|Instagram|Line|GSA|Twitter|LinkedInApp|OPT|EdgiOS|CriOS/i.test(ua);
}

function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /FBAV|FBAN|Instagram|Line|GSA|Twitter|LinkedInApp|OPT/i.test(ua);
}

export default function MobileInstallPrompt({ isMobile, isAuthenticated, variant = 'coach' }) {
  const parentVariant = variant === 'parent';
  const [deferredPrompt, setDeferredPrompt] = useState(() => capturedInstallPrompt);
  const [dismissed, setDismissed] = useState(() => {
    if (parentVariant) return false;
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [installed, setInstalled] = useState(() => (
    isStandaloneMode() || capturedAppInstalled || (parentVariant && isParentInstallConfirmed())
  ));
  const liveRegionRef = useRef(null);

  const ios = useMemo(() => isIosDevice(), []);
  const iosInApp = useMemo(() => isIosInAppBrowser(), []);
  const inAppBrowser = useMemo(() => isInAppBrowser(), []);

  useEffect(() => {
    const subscriber = ({ deferredPrompt: nextPrompt, installed: nextInstalled }) => {
      setDeferredPrompt(nextPrompt);
      if (nextInstalled) {
        setInstalled(true);
        if (parentVariant) confirmParentInstall();
      }
    };

    installPromptSubscribers.add(subscriber);
    subscriber({
      deferredPrompt: capturedInstallPrompt,
      installed: capturedAppInstalled,
    });
    return () => installPromptSubscribers.delete(subscriber);
  }, [parentVariant]);

  useEffect(() => {
    if (parentVariant && isStandaloneMode()) confirmParentInstall();
  }, [parentVariant]);

  const hidePrompt = () => {
    setDismissed(true);
    if (parentVariant) return;
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Ignore localStorage failures.
    }
  };

  const handleAlreadyInstalled = () => {
    confirmParentInstall();
    setInstalled(true);
    setDismissed(false);
  };

  const handleInstall = async () => {
    const installPrompt = deferredPrompt || capturedInstallPrompt;
    if (!installPrompt) return;
    installPrompt.prompt();
    try {
      await installPrompt.userChoice;
    } catch {
      // User closed prompt.
    }
    capturedInstallPrompt = null;
    setDeferredPrompt(null);
    notifyInstallPromptSubscribers();
  };

  const canShow = isMobile && isAuthenticated && !installed && !dismissed;
  const showIosHint = canShow && ios;
  const showInstallButton = canShow && !ios && !!deferredPrompt;
  const showParentFallback = canShow && parentVariant && !ios && !deferredPrompt;
  const showPrompt = showIosHint || showInstallButton || showParentFallback;
  const showParentLauncher = parentVariant && isMobile && isAuthenticated && !installed && dismissed;
  const showCoachLauncher = !parentVariant && isMobile && isAuthenticated && !installed && dismissed;

  useEffect(() => {
    if (!liveRegionRef.current) return;
    liveRegionRef.current.textContent = showPrompt
      ? (parentVariant ? 'Member Portal install suggestion available.' : 'EliteEdge install suggestion available.')
      : '';
  }, [parentVariant, showPrompt]);

  return (
    <>
      <Box
        component="span"
        ref={liveRegionRef}
        role="status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          p: 0,
          m: '-1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
      {showPrompt && <Box
        role="region"
        aria-label={parentVariant ? 'Install Member Portal suggestion' : 'Install EliteEdge suggestion'}
        sx={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: parentVariant
          ? 'calc(72px + env(safe-area-inset-bottom, 0px))'
          : COACH_INSTALL_BOTTOM,
        zIndex: 1250,
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        borderRadius: '10px',
        p: 1.25,
        boxShadow: '0 6px 18px rgba(0,0,0,0.25)'
        }}
      >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BrandLogo variant="icon" height={20} decorative />
        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '13px' }}>
          {parentVariant ? 'Add EliteEdge to your home screen' : 'Install EliteEdge app'}
        </Typography>
      </Box>
      {showIosHint ? (
        <Typography sx={{ color: 'var(--text-secondary)', fontSize: '12px', mt: 0.5 }}>
          {iosInApp
            ? 'Open this page in Safari, tap the Share icon, a square with an up arrow, then choose Add to Home Screen.'
            : 'On iPhone Safari: tap the Share icon, a square with an up arrow, then choose Add to Home Screen.'}
        </Typography>
      ) : showParentFallback ? (
        <Typography sx={{ color: 'var(--text-secondary)', fontSize: '12px', mt: 0.5 }}>
          {inAppBrowser
            ? 'Open this page in Chrome, then use the browser menu to choose Install app or Add to Home screen.'
            : 'Open the browser menu and choose Install app or Add to Home screen.'}
        </Typography>
      ) : (
        <Typography sx={{ color: 'var(--text-secondary)', fontSize: '12px', mt: 0.5 }}>
          {parentVariant
            ? "Open your child's updates quickly in a full-screen app experience."
            : 'Install for faster launch and a full-screen app experience.'}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {showInstallButton && (
          <Button
            aria-label={parentVariant ? 'Add EliteEdge to home screen' : 'Install EliteEdge app'}
            size="small"
            variant="contained"
            onClick={handleInstall}
            sx={{ textTransform: 'none', fontSize: '12px', minHeight: 44 }}
          >
            {parentVariant ? 'Add to home screen' : 'Install'}
          </Button>
        )}
        {parentVariant && !showInstallButton && (
          <Button
            aria-label="Member Portal is already installed"
            size="small"
            variant="outlined"
            onClick={handleAlreadyInstalled}
            sx={{ textTransform: 'none', fontSize: '12px', minHeight: 44 }}
          >
            I've added it
          </Button>
        )}
        <Button size="small" variant="text" onClick={hidePrompt} sx={{ textTransform: 'none', fontSize: '12px', minHeight: 44, color: 'var(--text-secondary)' }}>
          {parentVariant ? 'Not now' : "Don't show again"}
        </Button>
      </Box>
    </Box>}
      {showParentLauncher && (
        <Button
          aria-label="Open Member Portal install instructions"
          size="small"
          variant="contained"
          onClick={() => setDismissed(false)}
          sx={{
            position: 'fixed',
            right: 12,
            bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
            zIndex: 1240,
            textTransform: 'none',
            fontSize: '12px',
            minHeight: 44,
          }}
        >
          Install Member Portal
        </Button>
      )}
      {showCoachLauncher && (
        <Button
          aria-label="Open EliteEdge install instructions"
          size="small"
          variant="contained"
          onClick={() => setDismissed(false)}
          sx={{
            position: 'fixed',
            right: 12,
            bottom: COACH_INSTALL_BOTTOM,
            zIndex: 1240,
            textTransform: 'none',
            fontSize: '12px',
            minHeight: 44,
          }}
        >
          Install EliteEdge
        </Button>
      )}
    </>
  );
}
